import { createContext } from 'react';
import { ApplicationContextListenerScope } from '@philips-internal/oswald-ui-constants';
import { enumValues, isFunction } from '@philips-internal/oswald-ui-utils';
import { HistoryService } from '../../services/index.js';
class ApplicationContext {
    constructor() {
        this.listeners = enumValues(ApplicationContextListenerScope)
            .reduce((accumulator, scope) => accumulator.set(scope, new Set()), new Map());
        this.navigationStack = [];
        this.redirectListeners = new Set();
        this.title = '';
    }
    /**
     * Notify all listeners for a given scope.
     *
     * @param scope - the scope to be notified
     */
    notify(scope) {
        const scopeListeners = this.listeners.get(scope);
        scopeListeners?.forEach((listener) => listener());
    }
    /**
     * Notify all redirect listeners.
     *
     * @param args - the arguments to be received by the redirect listener
     */
    notifyRedirect(args) {
        this.redirectListeners.forEach((listener) => {
            listener(args);
        });
    }
    /**
     * Adds a listener to a given scope, which will be invoked when there's a change to a property of that scope.
     *
     * @param scope - the scope of the listener
     * @param listener - the listener function
     */
    addListener(scope, listener) {
        const scopeListeners = this.listeners.get(scope);
        if (!scopeListeners) {
            throw Error(`[Application Context]: Scope ${scope} is not initialized.`);
        }
        if (scopeListeners.has(listener)) {
            throw Error('[Application Context]: This listener is already being used.');
        }
        scopeListeners.add(listener);
        return () => {
            scopeListeners.delete(listener);
        };
    }
    /**
     * Adds a listener to the `ApplicationContextListenerScope.REDIRECT` scope, which will be invoked when a redirect action
     * occurs within the context.
     *
     * @param listener - the listener function
     */
    addRedirectListener(listener) {
        if (this.redirectListeners.has(listener)) {
            throw Error('[Application Context]: This listener is already being used.');
        }
        this.redirectListeners.add(listener);
        return () => {
            this.redirectListeners.delete(listener);
        };
    }
    /**
     * Clear the navigation stack of the application.
     *
     * @param fireListeners - If the navigation stack listeners should be called before cleaning.
     */
    clearNavigationStack(fireListeners = false) {
        if (fireListeners) {
            this.navigationStack.forEach((listener) => listener());
        }
        this.navigationStack = [];
        this.notify(ApplicationContextListenerScope.NAVIGATION);
    }
    /**
     * Obtain the size of the navigation stack.
     */
    getNavigationStackSize() {
        return this.navigationStack.length;
    }
    /**
     * Obtain the current application title.
     */
    getTitle() {
        return this.title;
    }
    /**
     * Indicates whether there's active navigation within the application.
     */
    hasNavigation() {
        return this.navigationStack.length > 0;
    }
    /**
     * Removes the last entry to the navigation stack of the application.
     */
    popNavigationItem() {
        if (!this.hasNavigation()) {
            return;
        }
        const callback = this.navigationStack.pop();
        if (isFunction(callback)) {
            callback();
        }
        this.notify(ApplicationContextListenerScope.NAVIGATION);
    }
    /**
     * Add a new entry to the navigation stack of the application.
     *
     * @param callback - the function to be called when the navigation stack is popped.
     */
    pushNavigationItem(callback) {
        this.navigationStack.push(callback);
        this.notify(ApplicationContextListenerScope.NAVIGATION);
        return () => {
            const position = this.navigationStack.indexOf(callback);
            if (position < 0) {
                return;
            }
            this.navigationStack.splice(position, 1);
            this.notify(ApplicationContextListenerScope.NAVIGATION);
        };
    }
    /**
     * Redirect the current path to a new one, resolving path parameters if necessary, adding a new item
     * to the navigation stack of the context.
     *
     * @param path - the path to be redirected to.
     * @param params - the path parameters.
     * @param options - the options which are used for the path change.
     */
    redirect(path, params, options) {
        const { pathname: previousPath = '' } = HistoryService.getMatch();
        HistoryService.push(path, params, options);
        this.pushNavigationItem(() => {
            HistoryService.back();
            this.notifyRedirect({ path: previousPath });
        });
        this.notifyRedirect({ path });
    }
    /**
     * Sets the application title.
     *
     * @param title - the new application title
     */
    setTitle(title) {
        this.title = title;
        this.notify(ApplicationContextListenerScope.TITLE);
    }
}
ApplicationContext.Context = createContext(new ApplicationContext());
export { ApplicationContext, ApplicationContextListenerScope, };
//# sourceMappingURL=application-context.js.map